import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import {
  INDEX_SYMBOL,
  fmtTime,
  shortMint,
} from "../lib/data";

const VOTE_API = "/api/vote";

function fmtUsdShort(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "—";
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

function errorCopy(code, extra = {}) {
  switch (code) {
    case "below_min_hold":
      return `Need ≥ ${(extra.minHolderTokens || 10000).toLocaleString()} $${INDEX_SYMBOL} in this wallet (on-chain). Found ${Number(extra.balanceUi || 0).toLocaleString()}.`;
    case "already_voted":
      return "This wallet already voted in the current poll.";
    case "bad_address":
      return "That doesn’t look like a valid Solana address.";
    case "not_a_candidate":
      return "Pick one of the listed candidates.";
    case "poll_closed":
      return "This poll just closed — refresh for the next round.";
    case "rate_limited":
      return "Too many attempts from this network — try again in a bit.";
    case "tx_required":
      return "Paste the Solana transaction signature after you send the dust + memo.";
    case "tx_not_found":
      return "Transaction not found yet — wait a few seconds for confirmation and retry.";
    case "tx_failed":
      return "That transaction failed on-chain.";
    case "tx_wrong_signer":
      return "Fee payer on the tx must be the wallet you’re voting with.";
    case "tx_no_treasury":
      return "Tx didn’t touch the treasury address — send dust to the address shown below.";
    case "tx_dust_too_low":
      return `Dust too low — need ≥ ${((extra.needLamports || 0) / 1e9).toFixed(6)} SOL to treasury.`;
    case "tx_bad_memo":
      return `Memo must be exactly: ${extra.expectedMemo || "SI-VOTE|…"} (copy from below).`;
    case "tx_already_used":
      return "That transaction was already used for a vote.";
    case "tx_too_old":
      return "Transaction is from before this poll opened.";
    default:
      return extra.error || code || "Vote failed.";
  }
}

function buildMemo(pollId, mint) {
  if (!pollId || !mint) return "";
  return `SI-VOTE|${pollId}|${mint}`;
}

export default function CommunityPage({ data }) {
  const seeded = data?.community || {};
  const [poll, setPoll] = useState(seeded.poll || null);
  const [pin, setPin] = useState(seeded.pin || null);
  const [address, setAddress] = useState("");
  const [choice, setChoice] = useState("");
  const [txSignature, setTxSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(null);

  const minHold = poll?.minHolderTokens ?? data?.schedule?.minHolderTokens ?? 10000;
  const verify = poll?.verify || {};
  const treasury = verify.treasury || null;
  const dustSol = verify.dustSol ?? (verify.dustLamports ? verify.dustLamports / 1e9 : 0.0001);
  const memo = buildMemo(poll?.id, choice);

  const totalVotes = useMemo(
    () => (poll?.candidates || []).reduce((s, c) => s + (Number(c.votes) || 0), 0),
    [poll],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(VOTE_API, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        if (body.poll) setPoll(body.poll);
        if (body.pin !== undefined) setPin(body.pin);
      } catch {
        /* keep snapshot seed */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function copyText(label, text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setErr("Could not copy — select and copy manually.");
    }
  }

  async function onVote(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await fetch(VOTE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: address.trim(),
          mint: choice,
          txSignature: txSignature.trim(),
        }),
      });
      const body = await res.json();
      if (body.poll) setPoll(body.poll);
      if (!body.ok) {
        setErr(errorCopy(body.error, body));
        return;
      }
      setMsg(`Counted — you voted for $${body.symbol}.`);
    } catch (ex) {
      setErr(ex.message || "Network error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="app-section" style={{ paddingTop: "2.5rem" }}>
      <div className="shell">
        <p className="section-kicker">
          <i className="brand-diamond" aria-hidden /> Community
        </p>
        <h2 className="section-title">Board seat vote</h2>
        <p className="section-lead left">
          Score picks most of the basket. Holders pick one discretionary seat.
          Prove you own the wallet by sending a dust of SOL to the treasury{" "}
          <strong>with an exact memo</strong>, then paste the tx id. Still need ≥
          {minHold.toLocaleString()} {`$${INDEX_SYMBOL}`}. One vote per address.
        </p>

        {pin ? (
          <aside className="spend-status" role="status">
            <div className="spend-status__head">
              <span className="spend-status__pill">Pinned</span>
              <strong>
                Community seat · ${pin.symbol || shortMint(pin.mint)}
              </strong>
            </div>
            <p>
              From poll {pin.pollId || "—"}. Active until{" "}
              {pin.until ? fmtTime(pin.until) : "next refresh"}.
            </p>
            <p className="spend-status__links">
              <Link href="/basket">View basket</Link>
            </p>
          </aside>
        ) : null}

        <div className="portfolio-summary" style={{ marginTop: pin ? "1.25rem" : 0 }}>
          <div className="portfolio-stat">
            <div className="label">Poll</div>
            <strong style={{ fontSize: "1.05rem" }}>{poll?.id || "—"}</strong>
            <p>{poll?.status === "open" ? "Open" : poll?.status || "Loading"}</p>
          </div>
          <div className="portfolio-stat">
            <div className="label">Closes</div>
            <strong style={{ fontSize: "1.05rem" }}>
              {poll?.closesAt ? fmtTime(poll.closesAt) : "—"}
            </strong>
            <p>Then winner pins to board</p>
          </div>
          <div className="portfolio-stat">
            <div className="label">Voters</div>
            <strong>{poll?.voterCount ?? 0}</strong>
            <p>equal — one ticket each</p>
          </div>
          <div className="portfolio-stat">
            <div className="label">Dust proof</div>
            <strong style={{ fontSize: "1.05rem" }}>{dustSol} SOL</strong>
            <p>+ memo binds your pick</p>
          </div>
        </div>

        {!poll && <p className="loading">Loading poll…</p>}

        {poll?.candidates?.length > 0 && (
          <form className="community-vote" onSubmit={onVote}>
            <ol className="community-steps">
              <li>Pick a candidate below.</li>
              <li>
                From the voting wallet, send{" "}
                <strong className="mono">{dustSol} SOL</strong> to the treasury
                with the memo shown (Phantom → Send → Memo).
              </li>
              <li>Paste your wallet address + the transaction signature here.</li>
            </ol>

            <div className="community-cands">
              {poll.candidates.map((c) => {
                const pct = totalVotes > 0 ? (c.votes / totalVotes) * 100 : 0;
                const selected = choice === c.mint;
                return (
                  <label
                    key={c.mint}
                    className={`community-cand${selected ? " is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="cand"
                      value={c.mint}
                      checked={selected}
                      onChange={() => setChoice(c.mint)}
                      disabled={poll.status !== "open" || busy}
                    />
                    <div className="community-cand__main">
                      <strong>${c.symbol}</strong>
                      <span>{c.name}</span>
                      <span className="mono community-cand__meta">
                        score {c.stonkScore ?? "—"} · mcap {fmtUsdShort(c.mcapUsd)} ·{" "}
                        {c.votes} vote{c.votes === 1 ? "" : "s"}
                      </span>
                      <div className="weight-bar" aria-hidden>
                        <span style={{ width: `${Math.max(c.votes > 0 ? 6 : 0, pct)}%` }} />
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="community-proof panel" style={{ marginTop: "1.25rem" }}>
              <h3 style={{ margin: "0 0 0.75rem", fontSize: "1.05rem" }}>
                Payment details
              </h3>
              <div className="field yield-field">
                <label>
                  Treasury
                  <span className="yield-hint">send dust here</span>
                </label>
                <div className="community-copyrow">
                  <code className="mono">{treasury || "—"}</code>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!treasury}
                    onClick={() => copyText("treasury", treasury)}
                  >
                    {copied === "treasury" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div className="field yield-field">
                <label>
                  Memo (exact)
                  <span className="yield-hint">pick a candidate first</span>
                </label>
                <div className="community-copyrow">
                  <code className="mono">{memo || "Select a candidate…"}</code>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={!memo}
                    onClick={() => copyText("memo", memo)}
                  >
                    {copied === "memo" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <p className="portfolio-footnote" style={{ margin: 0 }}>
                Without the memo, anyone could submit someone else’s tip and steal
                their vote. The memo locks this tx to your chosen mint.
              </p>
            </div>

            <div className="field yield-field" style={{ marginTop: "1.25rem" }}>
              <label htmlFor="vote-addr">
                Wallet address
                <span className="yield-hint">≥{minHold.toLocaleString()} ${INDEX_SYMBOL}</span>
              </label>
              <input
                id="vote-addr"
                className="mono"
                placeholder="Your Solana address (fee payer on the tx)"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={poll.status !== "open" || busy}
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            <div className="field yield-field">
              <label htmlFor="vote-tx">
                Transaction signature
                <span className="yield-hint">from Solscan / wallet history</span>
              </label>
              <input
                id="vote-tx"
                className="mono"
                placeholder="Paste tx signature"
                value={txSignature}
                onChange={(e) => setTxSignature(e.target.value)}
                disabled={poll.status !== "open" || busy}
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            {err ? <p className="error">{err}</p> : null}
            {msg ? <p className="community-ok">{msg}</p> : null}

            <div className="section-actions section-actions--row">
              <button
                type="submit"
                className="btn btn-lime"
                disabled={
                  poll.status !== "open" ||
                  busy ||
                  !choice ||
                  !address.trim() ||
                  !txSignature.trim()
                }
              >
                {busy ? "Verifying tx…" : "Submit vote"}
              </button>
              <Link href="/basket" className="btn btn-ghost">
                Current basket
              </Link>
            </div>
          </form>
        )}

        <p className="portfolio-footnote" style={{ marginTop: "1.5rem" }}>
          Anti-spam: dust tx from your key + exact memo + ≥{minHold.toLocaleString()}{" "}
          {`$${INDEX_SYMBOL}`} + one vote per address + IP limits. Each tx can only
          be used once. Dust goes to the fee treasury.
        </p>
      </div>
    </section>
  );
}
